import React, { useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
function FadeInUp({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.7,
    delay
  }} data-id="element-282">
      {children}
    </motion.div>;
}
export function ProblemSection() {
  return <section className="w-full py-20 px-4 relative" style={{
    background: 'linear-gradient(180deg, var(--color-navy) 0%, #0d1a30 50%, var(--color-navy) 100%)'
  }} data-id="element-283">
      <div className="max-w-3xl mx-auto" data-id="element-284">
        <FadeInUp data-id="element-285">
          <div className="text-center mb-12" data-id="element-286">
            <div className="section-divider mb-6" data-id="element-287">
              <span className="ornament" data-id="element-288">✦</span>
            </div>
            <h2 className="font-heading font-700 text-4xl md:text-5xl mb-4" style={{
            color: 'var(--color-cream)'
          }} data-id="element-289">
              La Industria de la Salud
              <span className="block italic" style={{
              color: 'var(--color-gold)'
            }} data-id="element-290">
                Te Ha Fallado
              </span>
            </h2>
          </div>
        </FadeInUp>

        <FadeInUp delay={0.1} data-id="element-291">
          <p className="font-body text-lg leading-relaxed mb-8 text-center" style={{
          color: 'var(--color-cream-dim)'
        }} data-id="element-292">
            Te han dicho que el declive físico es inevitable y te han
            acostumbrado a tratar solo los síntomas, nunca la raíz. Estamos
            viviendo en una epidemia de inflamación crónica causada por
            alimentos procesados y un estilo de vida que{' '}
            <strong style={{
            color: 'var(--color-cream)'
          }} data-id="element-293">
              "apaga" tus genes de longevidad.
            </strong>
          </p>
        </FadeInUp>

        <FadeInUp delay={0.2} data-id="element-294">
          <div className="rounded-xl p-8 mb-10 relative overflow-hidden" style={{
          background: 'rgba(15, 31, 61, 0.9)',
          border: '1px solid rgba(201, 168, 76, 0.2)'
        }} data-id="element-295">
            {/* Accent line */}
            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl" style={{
            background: 'linear-gradient(180deg, var(--color-gold), var(--color-gold-dim))'
          }} data-id="element-296" />
            <p className="font-heading text-xl md:text-2xl italic font-400 leading-relaxed pl-4" style={{
            color: 'var(--color-cream)'
          }} data-id="element-297">
              "El verdadero problema no es que estés envejeciendo; es que tus
              células están acumulando{' '}
              <span style={{
              color: 'var(--color-gold)'
            }} data-id="element-298">
                'basura biológica'
              </span>{' '}
              que nunca se limpia."
            </p>
          </div>
        </FadeInUp>

        <FadeInUp delay={0.3} data-id="element-299">
          <p className="font-body text-base md:text-lg leading-relaxed mb-10" style={{
          color: 'var(--color-cream-dim)'
        }} data-id="element-300">
            Esta acumulación es la responsable de la fatiga constante, la
            pérdida de memoria y el debilitamiento de tus huesos. Sin un proceso
            de limpieza profunda, tu cuerpo simplemente{' '}
            <strong style={{
            color: 'var(--color-cream)'
          }} data-id="element-301">
              se oxida por dentro.
            </strong>
          </p>
        </FadeInUp>

        <FadeInUp delay={0.35} data-id="element-302">
          <h3 className="font-heading font-600 text-2xl md:text-3xl mb-6 text-center" style={{
          color: 'var(--color-cream)'
        }} data-id="element-303">
            ¿Por Qué Lo Que Has Intentado{' '}
            <span style={{
            color: 'var(--color-gold)'
          }} data-id="element-304">
              No Ha Funcionado?
            </span>
          </h3>
        </FadeInUp>

        <div className="space-y-4" data-id="element-305">
          {[{
          icon: '✗',
          text: 'Las dietas de moda fallan porque solo cuentan calorías y olvidan la información genética que tus células necesitan para repararse.',
          delay: 0.4
        }, {
          icon: '✗',
          text: 'Los suplementos mágicos no funcionan si no activas primero tu mecanismo natural de autolimpieza celular (autofagia).',
          delay: 0.5
        }, {
          icon: '✗',
          text: 'El enfoque médico tradicional separa el cuerpo del espíritu, ignorando que la paz mental es el combustible del sistema inmune.',
          delay: 0.6
        }, {
          icon: '✗',
          text: 'Has intentado soluciones "rápidas" que no respetan los ritmos biológicos naturales de ayuno y nutrición densa.',
          delay: 0.7
        }].map((item, i) => <FadeInUp key={i} delay={item.delay} data-id="element-306">
              <div className="flex items-start gap-4 p-5 rounded-lg" style={{
            background: 'rgba(10, 22, 40, 0.6)',
            border: '1px solid rgba(201, 168, 76, 0.1)'
          }} data-id="element-307">
                <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-body font-700 text-sm mt-0.5" style={{
              background: 'rgba(180, 60, 60, 0.2)',
              color: '#e07070',
              border: '1px solid rgba(180, 60, 60, 0.3)'
            }} data-id="element-308">
                  {item.icon}
                </span>
                <p className="font-body text-base leading-relaxed" style={{
              color: 'var(--color-cream-dim)'
            }} data-id="element-309">
                  {item.text}
                </p>
              </div>
            </FadeInUp>)}
        </div>
      </div>
    </section>;
}